<script setup>
import {defineProps, defineEmits, ref, computed, onMounted, defineExpose, watch} from 'vue'
import Multiselect from '@vueform/multiselect'
import {debounce} from '@shared/utils/debounce'
import {useNewAdAccountRequestStore} from '@/views/ad-account-requests/AdAccountRequestStore'
import {useNotification} from '@kyvg/vue3-notification'
import {isEqual} from 'lodash'
import PlatformIcon from '@shared-components/ui/PlatformIcon.vue'

const {notify} = useNotification()
const {getAdAccounts} = useNewAdAccountRequestStore()

// Emits & props
const emit = defineEmits(['changed', 'update:modelValue', 'focusout', 'select-option', 'deselect'])
const props = defineProps({
  id: {
    type: String,
    default: 'ad-account-select',
  },
  modelValue: {},
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: 'Enter From 2 Characters to Search',
  },
  listBySearch: {
    type: Boolean,
    default: false,
  },
  payload: {
    type: Object,
    default: null,
  },
  valueToReturn: {
    type: String,
    default: 'id',
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  preselectOnce: {
    type: Boolean,
    default: false,
  },
  preselectAccount: {
    type: Object,
    default: null,
  },
})

// Refs
const adAccounts = ref([])
const loading = ref(false)
const mode = ref('single')
const fullSearchResultList = ref([])
let previousPayload = null
// entity_id is only unique within a single platform, so we track the selection with a
// platform-qualified key to keep options distinct across platforms (pinterest #3 vs mediago #3).
const selectedKey = ref(props.multiple ? [] : null)

// Computed
const isValid = computed(() => {
  if (props.multiple === true) {
    return !props.required || props.modelValue.length !== 0
  }

  return !props.required || !!props.modelValue
})

const makeOptionKey = account => `${account.platform}:${account.id}`

const adAccountsOptions = computed(() => {
  return adAccounts.value.map(account => {
    const {title, external_id, can_has_top_up, top_up_unavailable_reasons, platform, balance} = account
    let disabled = props.payload?.with_can_has_top_up && !can_has_top_up

    return {
      // Platform-qualified so options stay unique across platforms with colliding ids.
      value: makeOptionKey(account),
      label: title,
      sublabel: external_id,
      is_disabled: disabled,
      top_up_unavailable_reasons: top_up_unavailable_reasons ?? [],
      icon: platform,
      balance: balance,
    }
  })
})

// Methods
onMounted(async () => {
  await init()
})

const init = async () => {
  if (props.multiple === true) {
    mode.value = 'tags'
  }

  if (props.listBySearch) {
    await requestGetAdAccounts()
  }
}

const requestGetAdAccounts = async search => {
  try {
    adAccounts.value = await getAdAccounts({search, ...props.payload, with: ['company']})

    if (search === '  ' && fullSearchResultList.value.length === 0) {
      fullSearchResultList.value = cloneSafe(adAccounts.value)
    }
  } catch (responseError) {
    notify({type: 'error', text: responseError?.data.message})
  } finally {
    loading.value = false
  }
}

const handleSearch = value => {
  if ((value === '  ' || value === '') && fullSearchResultList.value.length > 0) {
    adAccounts.value = cloneSafe(fullSearchResultList.value)

    return
  }

  checkSearchValue(value)
}

const checkSearchValue = async searchValue => {
  if (searchValue?.length >= 2) {
    await reloadAdAccounts(searchValue)
  }
}

const reloadAdAccounts = debounce(async searchValue => {
  loading.value = true

  await requestGetAdAccounts(searchValue)
}, 300)

const returnValueOf = account => (props.valueToReturn === 'id' ? account.id : account.external_id)
const resolveByKey = key => adAccounts.value.find(account => makeOptionKey(account) === key)

// value is the platform-qualified option key(s); translate back to the id/external_id the
// parent expects and emit the exact selected account so entity_type matches the pick.
const onChange = value => {
  selectedKey.value = value

  if (props.multiple) {
    const accounts = (value ?? []).map(resolveByKey).filter(Boolean)
    emit('update:modelValue', accounts.map(returnValueOf))
    emit('changed', cloneSafe(accounts))

    return
  }

  const account = resolveByKey(value)
  emit('update:modelValue', account ? returnValueOf(account) : null)
  emit('changed', account ? cloneSafe(account) : null)
}

const onFocusOut = () => {
  emit('focusout')
  handleSearch('')
}
const onDeselect = () => {
  emit('deselect')
}

const getActiveOption = (value, option) => {
  emit('select-option', option)

  // entity_id is only unique within a single platform (each platform's ad_accounts table
  // has its own auto-increment), so a company can have e.g. pinterest #3 and mediago #3.
  // Resolve the exact account from the option the user actually picked, keyed by the
  // unique external_id + platform pair, so the emitted entity_type matches the selection.
  const account = adAccounts.value.find(
    item => item.external_id === option.sublabel && item.platform === option.icon,
  )

  emit('changed', cloneSafe(account))
}

watch(
  () => props.payload,
  newPayload => {
    if (!isEqual(newPayload, previousPayload)) {
      previousPayload = cloneSafe(newPayload)
      fullSearchResultList.value.length = 0
    }
  },
  {deep: true},
)

// Keep the internal selection key in sync with an externally-provided modelValue (reset,
// preselect) without clobbering a user's pick when ids collide across platforms.
watch(
  () => props.modelValue,
  value => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      selectedKey.value = props.multiple ? [] : null

      return
    }

    if (props.multiple) {
      return
    }

    const current = resolveByKey(selectedKey.value)
    if (current && returnValueOf(current) === value) {
      return
    }

    const account = adAccounts.value.find(item => returnValueOf(item) === value)
    if (account) {
      selectedKey.value = makeOptionKey(account)
    }
  },
)

// Deterministically preselect a specific account (e.g. Manage Funds opened from an account
// row). Load it by external_id, then select the exact one by platform + id so colliding ids
// across platforms can't select the wrong account.
watch(
  () => [props.preselectAccount, props.payload?.company_hash],
  async ([account, companyHash]) => {
    if (!account || selectedKey.value || !companyHash) {
      return
    }

    const key = makeOptionKey(account)

    loading.value = true
    // Title is far more selective than a short external_id (e.g. "1"), which produces an
    // over-broad, capped search that can miss the account. Load by title first, then fall
    // back to external_id, and select the exact account by platform + id.
    await requestGetAdAccounts(account.title || account.external_id || '  ')
    let match = adAccounts.value.find(item => makeOptionKey(item) === key)

    if (!match && account.external_id) {
      await requestGetAdAccounts(account.external_id)
      match = adAccounts.value.find(item => makeOptionKey(item) === key)
    }

    if (!match) {
      return
    }

    onChange(key)

    const option = adAccountsOptions.value.find(item => item.value === key)
    if (option) {
      emit('select-option', option)
    }
  },
  {immediate: true},
)

const stopPreselectWatch = watch(
  () => props.preselectOnce && mode.value === 'single' && adAccountsOptions.value.length === 1 && !props.modelValue,
  ok => {
    if (!ok) {
      return
    }

    const only = adAccountsOptions.value[0]
    onChange(only.value)
    getActiveOption(only.value, only)

    stopPreselectWatch()
  },
  {immediate: true},
)

defineExpose({handleSearch, loading})
</script>

<template>
  <div :class="['the-select-wrapper', isValid ? 'valid' : 'invalid', mode === 'tags' ? 'tags' : '']">
    <input v-if="required && !isValid" class="d-none" required />
    <Multiselect
      :value="selectedKey"
      :id="id"
      ref="multiSelect"
      :options="adAccountsOptions"
      @select="getActiveOption"
      :disabled="disabled"
      :track-by="['sublabel', 'label']"
      label="label"
      :mode="mode"
      searchable
      :placeholder="listBySearch ? 'Select Ad Account' : placeholder"
      :loading="loading"
      @input="onChange"
      @search-change="handleSearch"
      @focusout="onFocusOut"
      @deselect="onDeselect"
    >
      <template #clear><span /></template>

      <template #singlelabel="{value}">
        <div class="multiselect-single-label">
          <platform-icon :platform="value.icon" class="me-2" />
          <span class="multiselect-single-label-text text-start" dir="rtl">
            <bdi dir="ltr">{{ value.label }}</bdi>
          </span>
        </div>
      </template>
      <template #option="props">
        <platform-icon :platform="props.option.icon" class="me-2" />
        <div>
          <div>{{ props.option.label }}</div>
          <small v-if="props.option.sublabel" class="text-muted">{{ props.option.sublabel }}</small>
        </div>
      </template>
    </Multiselect>
  </div>
</template>
